import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { HouseFlat } from '../../models/house-flat.model';
import { ImageService } from '../../services/image.service';
import { NoticeViolationService } from '../../services/notice-violation.service';
import { SpinnerService } from '../../services/spinner.service';
import { StreetBuildingService } from '../../services/street-building.service';

@Component({
  selector: 'app-create-notice-violation-modal',
  templateUrl: './create-notice-violation-modal.component.html',
  styleUrl: './create-notice-violation-modal.component.scss'
})
export class CreateNoticeViolationModalComponent implements OnInit {

  @Input() neighborhoodId: string = '';

  /** Si viene, la casa ya está elegida (se abre desde el detalle de la casa). */
  @Input() streetBuildingId?: string;
  @Input() houseFlatId?: string;

  public readonly titleMaxLength: number = 45;

  public formNoticeViolation: FormGroup;
  public submitted: boolean = false;
  public loading: boolean = true;

  public housesFlats: HouseFlat[] = [];

  public uploadedImages: string[] = [];
  public pdfFiles: File[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private formBuilder: FormBuilder,
    private imageService: ImageService,
    public noticeViolationService: NoticeViolationService,
    private spinnerService: SpinnerService,
    public streetBuildingService: StreetBuildingService
  ) {
    this.formNoticeViolation = this.formBuilder.group({
      streetBuildingId: [null, Validators.required],
      houseFlatId: [null, Validators.required],
      title: ['', [Validators.required, Validators.maxLength(this.titleMaxLength)]],
      description: ['', Validators.required]
    });

    this.formNoticeViolation.controls['streetBuildingId'].valueChanges.subscribe(
      (streetBuildingId: string | null) => this.onStreetBuildingChange(streetBuildingId)
    );
  }

  get formControls() {
    return this.formNoticeViolation.controls;
  }

  get titleLength(): number {
    return (this.formControls['title'].value ?? '').length;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR ngOnInit                              */
  /* -------------------------------------------------------------------------- */
  // region ngOnInit
  async ngOnInit() {

    this.loading = true;

    await this.streetBuildingService.getAll(
      this.neighborhoodId
    );

    if (this.streetBuildingId) {
      this.formControls['streetBuildingId'].setValue(this.streetBuildingId);
    }

    if (this.houseFlatId) {
      this.formControls['houseFlatId'].setValue(this.houseFlatId);
    }

    this.loading = false;
  }

  /* -------------------------------------------------------------------------- */
  /*                         ANCHOR onStreetBuildingChange                      */
  /* -------------------------------------------------------------------------- */
  // region onStreetBuildingChange
  onStreetBuildingChange(
    streetBuildingId: string | null
  ) {

    const streetBuilding = this.streetBuildingService.streetsBuildings.find(
      (item) => item.streetBuildingId === streetBuildingId
    );

    this.housesFlats = streetBuilding ? streetBuilding.housesFlats : [];

    const currentHouseFlatId = this.formControls['houseFlatId'].value;

    /* Al cambiar de calle la casa elegida deja de ser válida, salvo que siga
       perteneciendo a la nueva calle (caso de la casa preseleccionada). */
    if (!this.housesFlats.some((item) => item.houseFlatId === currentHouseFlatId)) {
      this.formControls['houseFlatId'].setValue(null);
    }

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR compressFile                            */
  /* -------------------------------------------------------------------------- */
  // region compressFile
  async compressFile() {

    const result = await this.imageService.compressFile();
    this.uploadedImages.push(...result);

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR onPdfChange                             */
  /* -------------------------------------------------------------------------- */
  // region onPdfChange
  onPdfChange(
    event: Event
  ) {

    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);

    for (const file of files) {
      if (this.noticeViolationService.isValidPdf(file)) {
        this.pdfFiles.push(file);
      }
    }

    /* Se limpia el input para poder volver a elegir el mismo archivo. */
    input.value = '';

  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR removeImage                             */
  /* -------------------------------------------------------------------------- */
  // region removeImage
  removeImage(
    index: number
  ) {
    this.uploadedImages.splice(index, 1);
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR removePdf                              */
  /* -------------------------------------------------------------------------- */
  // region removePdf
  removePdf(
    index: number
  ) {
    this.pdfFiles.splice(index, 1);
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR onSubmit                              */
  /* -------------------------------------------------------------------------- */
  // region onSubmit
  async onSubmit() {

    this.submitted = true;

    if (this.formNoticeViolation.invalid) {
      return;
    }

    const noticeViolation = await this.noticeViolationService.create(
      this.neighborhoodId,
      {
        houseFlatId: this.formControls['houseFlatId'].value,
        title: this.formControls['title'].value.trim(),
        description: this.formControls['description'].value.trim()
      }
    );

    if (noticeViolation) {

      const files: Array<File | string> = [
        ...this.pdfFiles,
        ...this.uploadedImages
      ];

      for (let i = 0; i < files.length; i++) {

        const current = files[i];

        this.spinnerService.loading = true;

        await this.noticeViolationService.uploadFile(
          noticeViolation,
          current,
          typeof current === 'string'
            ? `Foto ${i - this.pdfFiles.length + 1}`
            : current.name,
          {
            index: i + 1,
            total: files.length
          }
        );

      }

      this.spinnerService.loading = false;
      this.spinnerService.message = '';

      await this.noticeViolationService.getAll(this.neighborhoodId);

      this.activeModal.close(noticeViolation);

    }

  }

}
